import React, { useState, useMemo } from 'react';
import { ChevronRight, Download, Eye } from 'lucide-react';
import { FONT_OPTIONS, COLOR_SCHEMES, LAYOUT_OPTIONS } from '../data/themeOptions';
import { FontOptions, ColorOptions, LayoutOptions, LivePreview } from './CustomizerOptions';
import { ClientSubmitForm } from './ClientSubmitForm';
import { WelcomeBanner } from './WelcomeBanner';

const INSTRUCTIONS = [
  'Click on any option to select your preferred fonts, colors, and layout',
  'Use "Show Preview" to see how your selections look together',
  'Fill in your name, contact info, and any notes in the form above',
  'Attach any files you\'d like to share (logo, photos, documents) and click "Send to My Designer" — your designer is notified automatically',
];

const CustomizerPage = () => {
  const [selectedFont, setSelectedFont] = useState('classic');
  const [selectedColor, setSelectedColor] = useState('navy-burgundy');
  const [selectedLayout, setSelectedLayout] = useState('default');
  const [showPreview, setShowPreview] = useState(false);

  const currentFont = FONT_OPTIONS.find(f => f.id === selectedFont);
  const currentColor = COLOR_SCHEMES.find(c => c.id === selectedColor);
  const currentLayout = LAYOUT_OPTIONS.find(l => l.id === selectedLayout);

  const selections = useMemo(
    () => ({ font: currentFont, color: currentColor, layout: currentLayout }),
    [currentFont, currentColor, currentLayout]
  );

  const handleDownloadSelections = () => {
    const blob = new Blob([JSON.stringify({ ...selections, timestamp: new Date().toISOString() }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bowtie-guy-design-selections-${Date.now()}.json`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#001B2A]">
                Website Customizer
              </h1>
              <p className="text-gray-600 mt-1">
                Preview and select your preferred design options
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <Eye className="h-5 w-5" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button
                onClick={handleDownloadSelections}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <Download className="h-5 w-5" />
                Download
              </button>
              <button
                data-testid="scroll-to-submit-button"
                onClick={() => document.getElementById('submit-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 px-4 py-2 bg-[#7B1E2B] text-white rounded-md hover:bg-[#001B2A] transition-colors"
              >
                Submit Selections
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeBanner />

        {/* Current Selections Summary */}
        <div className="bg-gradient-to-r from-[#001B2A] to-[#7B1E2B] rounded-lg p-6 mb-8 text-white">
          <h2 className="text-xl font-serif font-bold mb-4">Your Current Selections</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm opacity-80">Font Style</p>
              <p className="font-semibold">{currentFont.name}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Color Scheme</p>
              <p className="font-semibold">{currentColor.name}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">Layout</p>
              <p className="font-semibold">{currentLayout.name}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <FontOptions selected={selectedFont} onSelect={setSelectedFont} />
          <ColorOptions selected={selectedColor} onSelect={setSelectedColor} />
          <LayoutOptions selected={selectedLayout} onSelect={setSelectedLayout} />
        </div>

        {showPreview && <LivePreview font={currentFont} color={currentColor} />}

        <ClientSubmitForm selections={selections} />

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <ChevronRight className="h-5 w-5" />
            How to Use This Customizer
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            {INSTRUCTIONS.map((text, i) => (
              <li key={text} className="flex items-start gap-2">
                <span className="font-semibold">{i + 1}.</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CustomizerPage;
